import React, { useEffect, useMemo, useState } from "react";
import { format, isAfter, startOfDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DateRange } from "@/hooks/useDateRange";
import { useTranslation } from "react-i18next";

interface DateRangePickerProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  className?: string;
  /** Dates after this calendar day (local) cannot be selected. */
  maxDate?: Date;
  triggerVariant?: "icon-text" | "new_outline";
}

function clampDateToMax(date: Date | undefined, maxDate: Date | undefined) {
  if (!date || !maxDate) {
    return date;
  }

  const maxDay = startOfDay(maxDate);
  if (isAfter(startOfDay(date), maxDay)) {
    return maxDay;
  }

  return date;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  onDateRangeChange,
  className,
  maxDate,
  triggerVariant = "icon-text",
}) => {
  const { t } = useTranslation();
  const fromDateId = React.useId();
  const toDateId = React.useId();
  const [open, setOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Date | undefined>(
    dateRange.from,
  );
  const [tempToDate, setTempToDate] = useState<Date | undefined>(dateRange.to);

  const maxSelectableDay = useMemo(
    () => (maxDate ? startOfDay(maxDate) : undefined),
    [maxDate],
  );

  const isAfterMax = (date: Date) =>
    maxSelectableDay ? isAfter(startOfDay(date), maxSelectableDay) : false;

  useEffect(() => {
    setTempFromDate(dateRange.from);
    setTempToDate(dateRange.to);
  }, [dateRange.from, dateRange.to]);

  const formatDateRange = (range: DateRange): string => {
    if (!range.from && !range.to) {
      return t("common.dateRange.selectDates");
    }

    if (range.from && range.to) {
      const fromFormatted = format(range.from, "d MMM");
      const toFormatted = format(range.to, "d MMM");
      return `${fromFormatted} - ${toFormatted}`;
    }

    if (range.from) {
      return format(range.from, "d MMM");
    }

    return t("common.dateRange.selectDates");
  };

  const handleApply = () => {
    onDateRangeChange({
      from: clampDateToMax(tempFromDate, maxDate),
      to: clampDateToMax(tempToDate, maxDate),
    });
    setOpen(false);
  };

  const handleFromDateSelect = (date: Date | undefined) => {
    if (date && isAfterMax(date)) {
      return;
    }

    setTempFromDate(date);
    if (date && tempToDate && date > tempToDate) {
      setTempToDate(undefined);
    }
    if (date && tempToDate && isAfterMax(tempToDate)) {
      setTempToDate(undefined);
    }
  };

  const handleToDateSelect = (date: Date | undefined) => {
    if (date && isAfterMax(date)) {
      return;
    }

    if (!tempFromDate || !date) {
      setTempToDate(date);
      return;
    }

    if (date >= tempFromDate) {
      setTempToDate(date);
    }
  };

  const isFromDateDisabled = (date: Date) => {
    if (isAfterMax(date)) {
      return true;
    }

    return !!tempToDate && date > tempToDate;
  };

  const isToDateDisabled = (date: Date) => {
    if (isAfterMax(date)) {
      return true;
    }

    return !tempFromDate || date < tempFromDate;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} className={className}>
          <CalendarIcon />
          {formatDateRange(dateRange)}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.dateRange.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("common.dateRange.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor={fromDateId}>{t("common.dateRange.fromDate")}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id={fromDateId}
                  type="button"
                  variant="new_outline"
                  className="w-full justify-start text-left"
                >
                  <CalendarIcon />
                  {tempFromDate ? (
                    format(tempFromDate, "PPP")
                  ) : (
                    <span className="text-muted-foreground">
                      {t("common.dateRange.pickDate")}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={tempFromDate}
                  onSelect={handleFromDateSelect}
                  disabled={isFromDateDisabled}
                  toDate={maxDate}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={toDateId}>{t("common.dateRange.toDate")}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id={toDateId}
                  type="button"
                  variant="new_outline"
                  disabled={!tempFromDate}
                  className="w-full justify-start text-left"
                >
                  <CalendarIcon />
                  {tempToDate ? (
                    format(tempToDate, "PPP")
                  ) : (
                    <span className="text-muted-foreground">
                      {t("common.dateRange.pickDate")}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={tempToDate}
                  onSelect={handleToDateSelect}
                  disabled={isToDateDisabled}
                  toDate={maxDate}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="new_default" onClick={handleApply}>
            {t("common.dateRange.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
